import { ActionIcon, Button, Image, Modal, Switch } from '@mantine/core';
import { useState } from 'react';
import { ReactCompareSlider, ReactCompareSliderImage } from 'react-compare-slider';
import { IconChevronDown, IconChevronUp, IconReportSearch } from '@tabler/icons-react';
import { useDisclosure } from '@mantine/hooks';

export interface QualityControlGalleryModalProps {
  title: React.ReactNode;
  images: Record<string, string>;
  format: string;
  isSourceMax: boolean;
}

export function QualityControlGalleryModal({ title, images, format, isSourceMax }: QualityControlGalleryModalProps) {
  const [opened, { open, close }] = useDisclosure(false);
  const [selectedFrame, setSelectedFrame] = useState(0);
  const [showComparison, setShowComparison] = useState(true);

  const originalAssetMessage = isSourceMax ? 'glb Renders from DCC' : 'Original asset';
  const optimizedAssetMessage = `Optimized ${format} asset`;
  const diffImageMessage = 'Pixelwise difference image';

  // Filter images dynamically based on the key names
  const originalImages = Object.entries(images).filter(([key]) => key.startsWith('original'));
  const compImages = Object.entries(images).filter(([key]) => key.startsWith(format) && !key.includes('__diff'));
  const diffImages = Object.entries(images).filter(([key]) => key.startsWith(format) && key.includes('__diff'));

  const selectImage = (index: number) => setSelectedFrame(index);
  const previousImage = () => setSelectedFrame(Math.max(selectedFrame - 1, 0));
  const nextImage = () => setSelectedFrame(Math.min(selectedFrame + 1, originalImages.length - 1));

  const globalActiveStyle = { border: 'solid #38d9a9' };
  const invisibleStyle = { display: 'none' };

  return (
    <>
      <ActionIcon size='s' variant='default' onClick={open}>
        <IconReportSearch />
      </ActionIcon>
      <Modal
        opened={opened}
        onClose={close}
        title={title}
        size='40rem'
        style={{ display: 'flex', justifyContent: 'center' }}
      >
        <div style={{ display: 'flex', width: '100%', height: '100%' }}>
          <section>
            <div style={{ display: 'flex', justifyContent: 'space-around' }}>
              <p>{originalAssetMessage}</p>
              <p>{showComparison ? optimizedAssetMessage : diffImageMessage}</p>
            </div>
            {originalImages.map(([key, value], index) => {
              const comparisonVisible = index === selectedFrame && showComparison ? {} : invisibleStyle;
              const diffVisible = index === selectedFrame && !showComparison ? {} : invisibleStyle;
              return (
                <div key={value}>
                  <div style={{ ...comparisonVisible }}>
                    <ReactCompareSlider
                      itemOne={
                        <ReactCompareSliderImage src={value} alt={originalAssetMessage} title={originalAssetMessage} />
                      }
                      itemTwo={
                        <ReactCompareSliderImage
                          style={{ backgroundColor: 'white' }}
                          src={compImages[index]?.[1]} // Ensure image exists in compImages
                          alt={optimizedAssetMessage}
                          title={optimizedAssetMessage}
                        />
                      }
                    />
                  </div>
                  <div style={{ ...diffVisible }}>
                    <ReactCompareSlider
                      itemOne={
                        <ReactCompareSliderImage src={value} alt={originalAssetMessage} title={originalAssetMessage} />
                      }
                      itemTwo={
                        <ReactCompareSliderImage
                          style={{ backgroundColor: 'white' }}
                          src={diffImages[index]?.[1]} // Ensure image exists in diffImages
                          alt={diffImageMessage}
                          title={diffImageMessage}
                        />
                      }
                    />
                  </div>
                </div>
              );
            })}
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <Switch
                checked={!showComparison}
                onChange={(event: any) => setShowComparison(!event.currentTarget.checked)}
                label={diffImageMessage}
              />
            </div>
          </section>
          <section style={{ display: 'flex', flexDirection: 'column', width: '10rem', alignItems: 'center' }}>
            <Button style={{ width: '3.5rem' }} size='compact-xs' variant='default' onClick={previousImage}>
              <IconChevronUp />
            </Button>
            {originalImages.map(([key, value], index) => {
              const activeStyle = index === selectedFrame ? globalActiveStyle : {};
              return (
                <div key={index} onClick={() => selectImage(index)}>
                  <Image src={value} alt={key} style={{ ...activeStyle, width: '5rem', height: '5rem' }} />
                </div>
              );
            })}
            <Button style={{ width: '3.5rem' }} size='compact-xs' variant='default' onClick={nextImage}>
              <IconChevronDown />
            </Button>
          </section>
        </div>
      </Modal>
    </>
  );
}
